import { FC, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  formateTimeToAMPM,
  getAppointMentStatus,
  getFormatedDayDate,
} from "../../../../utils/DateUtils";
import {
  appointmentDetails,
  appointmentsStatus,
} from "../../../../types/calendar";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Button, Modal, message } from "antd";
import { CiCalendar } from "react-icons/ci";
import { FaRegCalendarCheck } from "react-icons/fa6";
import { LuCalendarX2 } from "react-icons/lu";
import { FaRegClock } from "react-icons/fa";
import queryClient from "../../../../services/react-query/queryClient";
import { adminUpdateAppointment } from "../../../../network/admin";
import { updateAppointment } from "../../../../network/calendar";

interface ModalHeaderProps {
  dateAndTime: {
    date: Date;
    startTime: string;
    finishTime?: string;
  };
  status: appointmentsStatus;
  appointment: appointmentDetails;
  canEdit: boolean;
  isAdmin?: boolean;
}
const bgColor = {
  completed: "[#479821]",
  started: "[#595FBC]",
  pending: "[#A9AEF6]",
  canceled: "[#EEF0FF]",
  created: "[#00086D]",
  overdue: "[#CF953D]",
  no_show: "",
  arrived: "",
};

const textColor = {
  completed: "white",
  started: "white",
  pending: "white",
  canceled: "",
  created: "white",
  overdue: "white",
  no_show: "",
  arrived: "",
};

const getAppointmentStatusColor = (status: appointmentsStatus) => {
  const className = `bg-${bgColor[status]} font-semibold text-${textColor[status]}`;
  return className;
};
const AppointmentModalHeader: FC<ModalHeaderProps> = ({
  dateAndTime,
  status,
  appointment,
  canEdit,
  isAdmin,
}) => {
  const intl = useIntl();
  const { currentLang } = useSelector(
    (state: { user: { currentLang: "en" | "ar" } }) => state.user
  );
  const [changedStatus, setChangedStatus] = useState<appointmentsStatus | null>(
    null
  );

  const { mutate } = useMutation(
    (body: {}) => {
      if (isAdmin) {
        return adminUpdateAppointment(
          appointment?.location_id,
          appointment?.id,
          body
        );
      } else {
        return updateAppointment(
          appointment?.location_id,
          appointment?.id,
          body
        );
      }
    },
    {
      onSuccess: (res: any) => {
        if (res?.code !== "ERR_BAD_REQUEST") {
          if (res?.response?.status >= 400) {
            message.error(res?.response?.data?.message);
            return;
          }
          queryClient.invalidateQueries(["appointment", appointment?.id]);
          queryClient.invalidateQueries(["appointments"]);
          message.success(intl.formatMessage({ id: "updatedSuccessfully" }));

          setChangedStatus(null);
        } else {
          message.error(
            res?.response?.data?.message ||
              intl.formatMessage({
                id: "somethingWentWrong",
                defaultMessage: "حدث خطأ",
              })
          );
        }
      },
    }
  );
  const handleChangeAppointmentStatus = () => {
    if (changedStatus) {
      mutate({
        status: changedStatus,
      });
    }
  };

  return (
    <div
      className={
        getAppointmentStatusColor(status) + " mt-4 rounded-lg p-3 text-sm "
      }
    >
      <div className="flex justify-between gap-2 ">
        <div className="space-y-4 ">
          <p className="flex gap-2 items-center">
            <span>
              {status === "completed" ? (
                <FaRegCalendarCheck size={30} />
              ) : status !== "canceled" ? (
                <CiCalendar size={30} />
              ) : (
                <LuCalendarX2 size={30} color="red" />
              )}
            </span>
            <span className="font-semibold">
              {getAppointMentStatus(status, currentLang)}
            </span>
          </p>
          <p className="flex gap-2 items-center ">
            <span>
              <FaRegClock />
            </span>
            <span className="text-semibold font-normal">
              {getFormatedDayDate(dateAndTime?.date, currentLang, true)}
              {/* {dateAndTime?.date?.toLocaleDateString()} */}
            </span>
          </p>
          {canEdit ? (
            status !== "canceled" && status !== "completed" ? (
              <div className="flex gap-2 items-center ">
                <p className="text-color-white">
                  {intl.formatMessage({
                    id: "updateAs",
                    defaultMessage: "تحديث كـ ",
                  })}
                </p>
                {status !== "started" ? (
                  <Button
                    type="primary"
                    htmlType="button"
                    className="bg-[#595FBC] ring text-color-white rounded-full"
                    onClick={() => {
                      setChangedStatus("started");
                    }}
                    style={{ borderColor: "#B0B0C0" }}
                  >
                    {intl.formatMessage({
                      id: "onProgress",
                      defaultMessage: "قيد التنفيذ ",
                    })}
                  </Button>
                ) : null}

                <Button
                  type="primary"
                  htmlType="button"
                  className="bg-[#595FBC] text-color-white rounded-full"
                  onClick={() => {
                    setChangedStatus("completed");
                  }}
                  style={{ borderColor: "#B0B0C0" }}
                >
                  {intl.formatMessage({
                    id: "completed",
                    defaultMessage: "مكتمل",
                  })}
                </Button>
              </div>
            ) : null
          ) : null}
        </div>
        <div className="space-y-4 self-center">
          <div className="flex items-center gap-1 ">
            <div className={"bg-white w-[6px] h-[6px] rounded-full"}></div>
            <span>
              {formateTimeToAMPM(dateAndTime?.startTime, currentLang)}{" "}
            </span>
            {dateAndTime?.finishTime ? (
              <>
                <span>{" - "}</span>
                <span>
                  {formateTimeToAMPM(dateAndTime?.finishTime, currentLang)}{" "}
                </span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      <Modal
        open={changedStatus !== null}
        footer={null}
        onCancel={() => setChangedStatus(null)}
        centered
        destroyOnClose
      >
        <div className="p-4 min-h-[200px] flex flex-col  gap-3 justify-center items-center">
          <div className="space-y-4">
            <h3 className="text-center text-color-primary">
              {intl.formatMessage({
                id: "changeAppStatus",
                defaultMessage: "هل انت متآكد من تغيير حالة الحجز؟",
              })}
            </h3>
            <p className="text-center text-color-sec-title">
              {intl.formatMessage({
                id: "changeAppStatusDesc",
                defaultMessage: "سيتم تغيير حالة الحجز للموعد المحدد",
              })}
            </p>
          </div>
          <div className="flex gap-4 w-full">
            <Button
              type="primary"
              htmlType="button"
              className="w-full"
              onClick={handleChangeAppointmentStatus}
            >
              {intl.formatMessage({
                id: "yes",
                defaultMessage: "تأكيد",
              })}
            </Button>

            <Button
              type="default"
              htmlType="button"
              className="w-full"
              onClick={() => setChangedStatus(null)}
            >
              {intl.formatMessage({
                id: "cancel",
                defaultMessage: "إلغاء",
              })}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AppointmentModalHeader;
